<!-- ══════════════════════════════════════════════════════════════════════════
     FICHA DEL COMERCIO — quién es y si puede vender. Nada más.

     🔴 REDISEÑO 2026-09-20, aprobado por Juan: *«cuando tú te metes a ver los
     comercios, da urticaria»*, *«demasiada caja, demasiado contenido, la vista
     se pierde»*.

     Este archivo tenía 1.843 líneas y SIETE pantallas adentro, como solapas:
     Identificación, Tiendas, Clientes, Cuentas por pagar, Cuenta, Contactos y
     Usuarios (esta última con dos sub-solapas más). Cada una se fue a su ruta
     propia —`/console/merchants/:code/tiendas`, `/cuentas`,
     `/clientes`, `/contactos`, `/usuarios`— y acá quedó la fila de accesos.
     Con ruta propia cada una tiene su enlace compartible, su atrás y su carga;
     apiladas en una solapa sólo las encontraba el que ya sabía que estaban ahí.

     Lo que salió, y por qué (medido antes de tocar nada):
       · LOS TRES AVISOS ÁMBAR APILADOS —«Sigue vendiendo», «Aprobado y no
         vuelve a revisión» y el de los datos para liquidar—. Nunca aparecían
         los tres a la vez y apilados dejan de advertir. Se resuelven en la
         insignia de estado de la cabecera y en UNA sola cinta.
       · LA CAJA DEL ENLACE AL PORTAL: 98 px de alto para mostrar una dirección
         que nadie lee de memoria. Pasó a dos botones de la cabecera.
       · LOS DOS PASOS `kb-steps` («El comercio la completa» / «El gerente
         aprueba»): decían lo mismo que las dos insignias que estaban tres
         centímetros más arriba.
       · El subtítulo de «Condición comercial vigente», que repetía el título.

     Y entró un dato que estaba guardado y no se mostraba en NINGUNA de las
     siete pantallas: QUIÉN FIRMA por el comercio (`legal_rep` del modelo).
     ══════════════════════════════════════════════════════════════════════════ -->
<div class="kb-page kb-gateway">

  <!-- ── Migas ─────────────────────────────────────────────────────────────── -->
  <nav class="kb-migas" aria-label="Ubicación">
    <a class="kb-miga !text-muted-foreground hover:!text-foreground" [routerLink]="['/console/merchants']">Comercios</a>
    <span class="kb-miga-sep" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
    <span class="kb-miga-actual">{{ comercio?.name || codigo }}</span>
  </nav>

  <!-- ── Cargando ──────────────────────────────────────────────────────────── -->
  <!-- El esqueleto conserva la FORMA de lo que viene: título, fila de accesos y
       dos bloques de pares. Con otra silueta, la pantalla salta al cargar. -->
  <div *ngIf="loading" class="space-y-8" aria-live="polite" aria-busy="true">
    <div class="kb-skeleton h-9 w-1/3"></div>
    <div class="kb-skeleton h-16 w-full rounded-lg"></div>
    <div class="space-y-4">
      <div class="kb-skeleton h-3 w-28"></div>
      <div class="kb-skeleton h-5 w-full max-w-lg" *ngFor="let s of [1,2,3,4]"></div>
    </div>
  </div>

  <!-- ── No existe ─────────────────────────────────────────────────────────── -->
  <div class="kb-empty" *ngIf="!loading && noEncontrado">
    <span class="kb-empty-icono"><i class="fas fa-store-slash" aria-hidden="true"></i></span>
    <p class="kb-empty-titulo">No encontramos el comercio «{{ codigo }}»</p>
    <p class="kb-empty-texto">Puede que se haya eliminado o que el código esté mal escrito.</p>
    <button type="button" class="kb-btn kb-btn-outline mt-4" (click)="volver()">
      <i class="fas fa-arrow-left" aria-hidden="true"></i>Volver al listado
    </button>
  </div>

  <ng-container *ngIf="!loading && comercio">

    <!-- ══════════════ CABECERA ═══════════════════════════════════════════════
         El nombre, UNA insignia que contesta «¿puede vender?», el renglón de
         identificación y los botones con su objeto. -->
    <div class="kb-page-header">
      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-3">
          <!-- El icono al lado del nombre, como en la ficha de HZ Notificaciones
               (`<Building2 className="h-6 w-6" />` junto al h1). Atado al tamaño
               de la letra con `text-[0.85em]`: si el título cambia de escala, el
               icono lo sigue en vez de quedarse suelto. -->
          <h1 class="kb-page-title flex min-w-0 items-center gap-2.5 !font-bold">
            <!-- La estampa del comercio (ESC-05; Mario, 12-oct) en lugar del ícono de tienda -->
            <app-estampa-comercio [codigo]="comercio.code" [tam]="48"></app-estampa-comercio>
            <span class="truncate">{{ comercio.name }}</span>
          </h1>
          <span class="kb-badge" *ngIf="esProveedor" style="background:#ede9fe;color:#5b21b6">Proveedor de servicio</span>
          <span class="kb-badge" [ngClass]="estadoVenta.clase">
            <i class="fas" [ngClass]="estadoVenta.icono" aria-hidden="true"></i>{{ estadoVenta.texto }}
          </span>
        </div>
        <p class="kb-page-meta">
          <span class="font-mono">{{ comercio.code }}</span>
          <ng-container *ngIf="comercio.legal_name"> &middot; {{ comercio.legal_name }}</ng-container>
          <ng-container *ngIf="comercio.tax_id"> &middot; <span class="font-mono">{{ comercio.tax_id }}</span></ng-container>
          <ng-container *ngIf="comercio.approved_at">
            &middot; ficha aprobada el {{ comercio.approved_at | date: 'dd/MM/yyyy' }}
          </ng-container>
          <ng-container *ngIf="!comercio.approved_at"> &middot; ficha sin aprobar</ng-container>
          <!-- De qué solicitud nació (el comercio solo nace del alta). -->
          <ng-container *ngIf="comercio.origen_alta?.numero"> &middot; nació de la solicitud
            <a class="!text-foreground underline underline-offset-2" [routerLink]="['/console/altas', comercio.origen_alta.solicitud]">{{ comercio.origen_alta.numero }}</a>
          </ng-container>
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2 sm:shrink-0">
        <!-- 2026-09-24, gramática del Gateway: el principal en negro y primero; los demás con
             borde. Si la ficha espera el visto bueno, el principal es «Aprobar y habilitar». -->
        <button data-rbac-permiso="config.comercios.aprobar" type="button" class="kb-btn kb-btn-primary !bg-foreground !text-background hover:!bg-foreground/90"
                *ngIf="puede('update') && !(esperaGerente && _auth.hasPermission('config.comercios.aprobar'))"
                (click)="editar()">
          <i class="fas fa-pen" aria-hidden="true"></i>Editar la ficha
        </button>
        <!-- El visto bueno del gerente: es LA decisión de esta pantalla. -->
        <button data-rbac-permiso="config.comercios.aprobar" type="button" class="kb-btn kb-btn-primary !bg-foreground !text-background hover:!bg-foreground/90"
                *ngIf="esperaGerente && _auth.hasPermission('config.comercios.aprobar')"
                [disabled]="revisando" (click)="aprobarFicha()">
          <i class="fas fa-thumbs-up" aria-hidden="true"></i>Aprobar y habilitar
        </button>
        <button data-rbac-permiso="config.comercios.aprobar" type="button" class="kb-btn kb-btn-outline"
                *ngIf="(esperaGerente || estadoFicha === 'APROBADA') && _auth.hasPermission('config.comercios.aprobar')"
                [disabled]="revisando" (click)="pedirCambios()">
          <i class="fas fa-undo !text-foreground" aria-hidden="true"></i>Devolver la ficha
        </button>


        <button type="button" class="kb-btn kb-btn-outline"
                *ngIf="puede('suspend') && comercio.state === 'ACTIVE'"
                (click)="cambiarEstado('SUSPENDED')">
          <i class="fas fa-pause-circle !text-foreground" aria-hidden="true"></i>Suspender el comercio
        </button>
        <button type="button" class="kb-btn kb-btn-outline"
                *ngIf="puede('suspend') && comercio.state !== 'ACTIVE'"
                (click)="cambiarEstado('ACTIVE')">
          <i class="fas fa-play-circle !text-foreground" aria-hidden="true"></i>Reactivar el comercio
        </button>
      </div>
    </div>

    <!-- ══════════════ LA CINTA — una sola, y sólo si hay algo que advertir ═══
         Acá había TRES avisos ámbar uno debajo del otro. El estado lo dice la
         insignia de la cabecera; esto dice la CONSECUENCIA, que es lo que la
         insignia no puede decir. El orden de prioridad está en el componente. -->
    <div class="kb-cinta" *ngIf="aviso" [class.kb-cinta-info]="aviso.tono === 'info'">
      <i class="fas kb-cinta-icono"
         [ngClass]="aviso.tono === 'info' ? 'fa-info-circle' : 'fa-exclamation-triangle'"
         aria-hidden="true"></i>
      <p class="m-0">{{ aviso.texto }}</p>
    </div>

    <!-- ══════════════ LA TIRA DEL PORTAL ═════════════════════════════════════
         🔴 VOLVIÓ, y la había sacado yo por error. La primera vuelta la borró
         diciendo «98 px de alto para mostrar una dirección», y al mirar la
         pantalla de HZ Notificaciones —que Juan puso como referencia— resulta
         que ellos SÍ la tienen: pero como una TIRA de un renglón, no como una
         caja con título. Eso era lo que estaba mal, no que existiera.

         Y se gana su lugar: es la dirección que se le pasa al comercio para que
         entre, así que se copia, no se recuerda. Un botón suelto en la cabecera
         no deja verla antes de copiarla. -->
    <div class="kb-tira" *ngIf="!esProveedor">
      <p class="m-0 min-w-0 text-sm">
        <span class="text-muted-foreground">Su portal:&nbsp;</span>
        <span class="break-all font-mono text-foreground">{{ urlPortal }}</span>
      </p>
      <div class="flex shrink-0 items-center gap-2">
        <button type="button" class="kb-btn kb-btn-outline kb-btn-sm"
                (click)="copiar(urlPortal, 'Dirección del portal copiada')">
          <i class="fas fa-copy !text-foreground" aria-hidden="true"></i>Copiar
        </button>
        <a class="kb-btn kb-btn-ghost kb-btn-sm" [href]="urlPortal"
           target="_blank" rel="noopener noreferrer">
          <i class="fas fa-external-link-alt !text-foreground" aria-hidden="true"></i>Abrir
        </a>
      </div>
    </div>

    <!-- ══════════════ LOS ACCESOS — una celda por pantalla hija ══════════════
         Reemplazan a las siete solapas. Cada una es una ruta de verdad: enlace
         compartible, atrás del navegador y carga propia.

         🔴 LAS SIETE LLEVAN SU NÚMERO. Tres de ellas —clientes, por pagar y
         usuarios— se piden al servidor DESPUÉS de que la ficha pinta, las tres
         en paralelo, y muestran su esqueleto mientras llegan (ver `cargarConteos`
         en el componente). La versión anterior puso la palabra «Ver» en esas
         tres para ahorrarse las llamadas, y Juan lo rechazó: una fila de accesos
         donde tres celdas no dicen nada no informa. El conteo ES el dato por el
         que se entra. -->
    <!-- ══════════════ LAS PESTAÑAS ══════════════════════════════════════════
         🔴 ERA UNA HILERA DE SIETE CAJAS CON LÍNEAS, y Juan la rechazó:
         *«ese menú está horrible… mira cómo se sale todo»*. Con siete celdas de
         ancho fijo, la última («Condiciones») quedaba cortada fuera de la
         pantalla — y cada celda pintaba su borde, así que se leía como una tabla
         de métricas y no como una navegación.

         Ahora es la pastilla segmentada de HZ Notificaciones (`Datos · Envíos ·
         Dinero`): un carril gris, la activa en blanco con su sombra, y el conteo
         como acompañante del rótulo, no como un número grande debajo.

         🔴 Y CADA UNA ES UNA RUTA HIJA, no otra pantalla. Lo pidió así:
         *«cámbiame la información que está abajo, en vez de pasarme por otro
         componente que después ni siquiera el atrás tiene»*. El encabezado, la
         tira del portal y estas pestañas se quedan fijos; sólo se repinta el
         `<router-outlet>`. Y como sigue siendo una ruta, el enlace se comparte y
         el atrás funciona — que es lo que un cambio de pestaña en memoria no da.

         `overflow-x-auto` en el carril: en una pantalla angosta se desliza en vez
         de cortarse, que era el defecto que originó este cambio. -->
    <div class="overflow-x-auto">
      <!-- RBAC (2026-10-06): cada pestaña con su permiso de «Consultar». La ficha
           va con config.comercios.read, que ya pide el menú para llegar acá. Desde 2026-11-02 Tiendas, Contactos, Odoo y
           Asistente de IA tienen fila propia en Multicomercio → Comercios (config.comercios_tiendas / _contactos / _odoo / _asistente).
           Sin RBAC, hasAnyPermission deja pasar todo (compatibilidad). -->
      <nav class="kb-tabs-list" aria-label="Secciones del comercio">
        <a class="kb-pestana" [routerLink]="['/console/merchants', codigo]"
           routerLinkActive="kb-pestana-activa" [routerLinkActiveOptions]="{ exact: true }">
          Ficha
        </a>
        <a data-rbac-permiso="config.comercios_tiendas.read" class="kb-pestana" *ngIf="_auth.hasAnyPermission(['config.comercios_tiendas.read'])" [routerLink]="['/console/merchants', codigo, 'tiendas']"
           routerLinkActive="kb-pestana-activa">
          Tiendas
        </a>
        <a data-rbac-permiso="config.comercios_cuentas.read" class="kb-pestana" *ngIf="_auth.hasAnyPermission(['config.comercios_cuentas.read'])" [routerLink]="['/console/merchants', codigo, 'cuentas']"
           routerLinkActive="kb-pestana-activa">
          Cuentas
        </a>
        <!-- 🔴 2026-10-07, Mario: «Por pagar» sale de la ficha. Los pagos a comercios se ven y se hacen
             en el módulo de Liquidación (todo pago va por lote, sin registro manual). -->
        <!-- 🔴 2026-09-24, Juan: «Clientes» sale de las pestañas —«los clientes no son del comercio,
             son míos»—. La ruta sigue existiendo; sólo deja de ofrecerse aquí. -->
        <a data-rbac-permiso="config.comercios_contactos.read" class="kb-pestana" *ngIf="_auth.hasAnyPermission(['config.comercios_contactos.read'])" [routerLink]="['/console/merchants', codigo, 'contactos']"
           routerLinkActive="kb-pestana-activa">
          Contactos
        </a>
        <a data-rbac-permiso="config.comercios_usuarios.read" class="kb-pestana" *ngIf="!esProveedor && _auth.hasAnyPermission(['config.comercios_usuarios.read'])" [routerLink]="['/console/merchants', codigo, 'usuarios']"
           routerLinkActive="kb-pestana-activa">
          Usuarios
        </a>
        <a data-rbac-permiso="config.comercios.terms.read" class="kb-pestana" *ngIf="_auth.hasAnyPermission(['config.comercios.terms.read'])" [routerLink]="['/console/merchants', codigo, 'condiciones']"
           routerLinkActive="kb-pestana-activa">
          Condiciones
        </a>
        <a data-rbac-permiso="config.comercios_odoo.read" class="kb-pestana" *ngIf="_auth.hasPermission('config.comercios_odoo.read')"
           [routerLink]="['/console/merchants', codigo, 'odoo']" routerLinkActive="kb-pestana-activa">
          Odoo
        </a>
        <!-- 2026-09-25: el tema del portal del comercio (Principal, CrediTotal, CrediTotal 2 o
             Personalizado). La ve quien entra a la ficha; guardar pide `config.comercios.update`. -->
        <a data-rbac-permiso="config.comercios_temas.read" class="kb-pestana" *ngIf="!esProveedor && _auth.hasAnyPermission(['config.comercios_temas.read'])" [routerLink]="['/console/merchants', codigo, 'temas']"
           routerLinkActive="kb-pestana-activa">
          Temas
        </a>
        <a data-rbac-permiso="config.comercios_cierre_caja.read" class="kb-pestana" *ngIf="!esProveedor && _auth.hasAnyPermission(['config.comercios_cierre_caja.read'])" [routerLink]="['/console/merchants', codigo, 'cierre-de-caja']"
           routerLinkActive="kb-pestana-activa">
          Cierre de caja
        </a>
        <!-- 2026-09-25: el asistente de IA del comercio (CONTRATO de `modules/ia` §8.2). Misma
             regla que `puedeVerIA` de `services/ia/ia-formato.ts`, pero con RBAC la pestaña pide
             `config.comercios_asistente.read`; sin RBAC, el perfil OWNER. -->
        <a data-rbac-permiso="config.comercios_asistente.read" class="kb-pestana" [routerLink]="['/console/merchants', codigo, 'asistente']"
           routerLinkActive="kb-pestana-activa"
           *ngIf="!esProveedor && (_auth.hasRbac$.getValue()
                  ? _auth.hasAnyPermission(['config.comercios_asistente.read'])
                  : _auth.IsPermitid(['OWNER']))">
          Asistente de IA
        </a>
      </nav>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════
         🔴 DOS COLUMNAS, Y NO ES ADORNO.

         La primera versión apiló las tres secciones en una sola columna sobre
         una página de 1.400 px: la mitad derecha de la pantalla quedaba en
         blanco y el ojo tenía que recorrer hacia abajo algo que cabía al lado.
         Juan lo rechazó — *«te fuiste solo a lo minimalista, no tiene detalle»*.

         Quitar cajas es diseño; dejar media pantalla vacía es desperdiciarla.
         Son dos cosas distintas y la primera vuelta las confundió.

         El reparto no es por tamaño: es por lo que se consulta. A la izquierda
         QUIÉN ES el comercio —lo que se mira para reconocerlo o para corregir
         un dato—; a la derecha CÓMO SE LE PAGA y QUIÉN LO APROBÓ, que es lo que
         se mira cuando hay que liquidar o cuando algo salió mal.
         ══════════════════════════════════════════════════════════════════════ -->
    <!-- ══════════════ LO QUE CAMBIA AL TOCAR UNA PESTAÑA ════════════════════
         🔴 Acá abajo se repinta, y sólo acá. Arriba queda fijo el encabezado, la
         cinta, la tira del portal y la barra — que es lo que Juan pidió:
         *«cámbiame la información que está abajo»*. Las seis hijas entran por
         este `<router-outlet>`; la ficha en sí es la ruta vacía. -->
    <router-outlet></router-outlet>

    <!-- Juan, 2026-10-22: las cajas una debajo de la otra, cada una a lo ancho y con aire entre ellas
         (antes iban de a dos por fila y se veía todo pegado). Adentro, los datos en tres columnas. -->
    <div class="grid gap-6" *ngIf="enLaFicha">

    <!-- ══════════════ ALTA DEL COMERCIO ═══════════════════════════════════════
         2026-09-24: las dos mitades de lo que le falta a un comercio para vender. A la izquierda,
         lo que pide su ficha —la MISMA lista que ve él en «Mi comercio», leída del núcleo—; a la
         derecha, lo que le toca a CrediTotal. Se muestra mientras falte algo de cualquiera de
         los dos lados. Cada pendiente de CrediTotal lleva a la pestaña donde se resuelve. -->
    <section class="kb-tarjeta lg:col-span-2" *ngIf="avance || faltaDeCrediTotal">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-tasks text-muted-foreground" aria-hidden="true"></i>Alta del comercio
          </h2>
          <p class="m-0 text-sm text-muted-foreground">Lo que falta, de cada lado, para que pueda vender.</p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">
        <div class="grid gap-x-[32px] gap-y-[24px] lg:grid-cols-2">
          <div class="space-y-2" *ngIf="avance">
            <p class="m-0 text-sm font-medium text-foreground">Del comercio · su ficha</p>
            <p class="m-0 text-xs text-muted-foreground">
              {{ estadoFicha === 'ENVIADA' || estadoFicha === 'REVISADA'
                 ? 'La completó y la envió: falta el visto bueno.'
                 : avance.completa
                   ? 'Está completa: ya puede enviarla a revisión.'
                   : 'Lo que todavía tiene que completar. También se puede cargar desde «Editar la ficha».' }}
            </p>
            <ul class="m-0 grid list-none gap-2 p-0 sm:grid-cols-2">
              <li class="!my-0 flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                  *ngFor="let r of avance.detalle"
                  [ngClass]="{ 'border-amber-300 dark:border-amber-500/40': !r.listo }">
                <i class="fas" aria-hidden="true"
                   [ngClass]="r.listo ? 'fa-check text-emerald-600' : 'fa-exclamation-circle text-amber-600'"></i>
                <span class="flex-1">{{ r.label }}</span>
                <span class="text-xs font-medium text-amber-700" *ngIf="!r.listo">Falta</span>
                <span class="sr-only" *ngIf="r.listo">listo</span>
              </li>
            </ul>
            <p class="kb-hint">Los documentos no frenan el envío; quien revisa los pide si hacen falta.</p>
          </div>
          <div class="space-y-2">
            <p class="m-0 text-sm font-medium text-foreground">De CrediTotal</p>
            <p class="m-0 text-xs text-muted-foreground">Lo que le toca a CrediTotal para que el comercio pueda vender y cobrar.</p>
            <ul class="m-0 grid list-none gap-2 p-0">
              <li class="!my-0" *ngFor="let x of deCrediTotal">
                <a *ngIf="!x.listo && x.ruta" [routerLink]="['/console/merchants', codigo, x.ruta]"
                   class="flex items-center gap-2 rounded-md border border-amber-300 px-3 py-2 text-sm !text-foreground no-underline hover:bg-accent dark:border-amber-500/40">
                  <i class="fas fa-exclamation-circle text-amber-600" aria-hidden="true"></i>
                  <span class="flex-1">{{ x.label }} <span class="text-xs text-muted-foreground" *ngIf="x.nota">· {{ x.nota }}</span></span>
                  <span class="text-xs font-medium text-amber-700">Resolver <i class="fas fa-chevron-right ml-0.5 text-[10px]" aria-hidden="true"></i></span>
                </a>
                <span *ngIf="!x.listo && !x.ruta"
                      class="flex items-center gap-2 rounded-md border border-amber-300 px-3 py-2 text-sm dark:border-amber-500/40">
                  <i class="fas fa-exclamation-circle text-amber-600" aria-hidden="true"></i>
                  <span class="flex-1">{{ x.label }} <span class="text-xs text-muted-foreground" *ngIf="x.nota">· {{ x.nota }}</span></span>
                  <span class="text-xs font-medium text-amber-700">Falta</span>
                </span>
                <span *ngIf="x.listo" class="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                  <i class="fas fa-check text-emerald-600" aria-hidden="true"></i>
                  <span class="flex-1">{{ x.label }}</span><span class="sr-only">listo</span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════════ IDENTIFICACIÓN ═════════════════════════════════════════ -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-id-card text-muted-foreground" aria-hidden="true"></i>Identificación
          </h2>
          <p class="m-0 text-sm text-muted-foreground">Con qué figura el comercio en los documentos que se emiten a su nombre.</p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">

        <dl class="grid gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <!-- 🔴 El código volvió. Estaba sólo en el renglón de arriba, mezclado con
             el resto del meta: es el dato con el que se busca este comercio en
             cualquier otra pantalla y tiene que poder copiarse de un renglón. -->
        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Código</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span class="inline-flex items-center gap-1">
            <code class="rounded bg-muted !text-foreground px-2 py-1 font-mono text-sm">{{ comercio.code }}</code>
            <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon h-7 w-7" title="Copiar el código"
                    aria-label="Copiar el código" (click)="copiar(comercio.code, 'Código copiado')">
              <i class="fas fa-copy text-xs !text-foreground" aria-hidden="true"></i></button>
          </span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Razón social</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span  *ngIf="comercio.legal_name">{{ comercio.legal_name }}</span>
          <span class="kb-dato-falta" *ngIf="!comercio.legal_name">
            <i class="fas fa-exclamation-circle mr-1.5" aria-hidden="true"></i>Falta
          </span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">RIF</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <code class="rounded bg-muted !text-foreground px-2 py-1 font-mono text-sm" *ngIf="comercio.tax_id">{{ comercio.tax_id }}</code>
          <span class="kb-dato-falta" *ngIf="!comercio.tax_id">
            <i class="fas fa-exclamation-circle mr-1.5" aria-hidden="true"></i>Falta
          </span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Domicilio fiscal</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span *ngIf="comercio.fiscal?.street">
            {{ comercio.fiscal.street }}<ng-container *ngIf="comercio.fiscal.city">, {{ comercio.fiscal.city }}</ng-container><ng-container *ngIf="comercio.fiscal.municipality">, {{ comercio.fiscal.municipality }}</ng-container><ng-container *ngIf="comercio.fiscal.state">, {{ comercio.fiscal.state }}</ng-container><ng-container *ngIf="comercio.fiscal.postal_code"> · CP {{ comercio.fiscal.postal_code }}</ng-container>
          </span>
          <span *ngIf="!comercio.fiscal?.street && comercio.address">{{ comercio.address }}</span>
          <span class="kb-dato-falta" *ngIf="!comercio.fiscal?.street && !comercio.address">
            <i class="fas fa-exclamation-circle mr-1.5" aria-hidden="true"></i>Falta
          </span>
          </dd>
        </div>

        <!-- 🔴 QUIÉN FIRMA POR EL COMERCIO. Estaba guardado (`legal_rep` en el
             modelo, con nombre, cédula y cargo) y no se mostraba en ninguna de
             las siete pantallas: el ejecutivo tenía que pedirlo por teléfono
             igual que antes de que existiera el campo.
             📍 No es una autorización de pago, es dato de la ficha — por eso va
             acá y no cerca de la cuenta de liquidación. -->
        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Quién firma</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span  *ngIf="representante">{{ representante }}</span>
          <span class="kb-dato-nota mt-0.5 block" *ngIf="representante && representanteDetalle">
            {{ representanteDetalle }}
          </span>
          <span class="text-muted-foreground" *ngIf="!representante">Sin registrar</span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Teléfono</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span  *ngIf="comercio.phone">{{ comercio.phone }}</span>
          <span class="text-muted-foreground" *ngIf="!comercio.phone">Sin registrar</span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Correo</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span  *ngIf="comercio.email">{{ comercio.email }}</span>
          <span class="text-muted-foreground" *ngIf="!comercio.email">Sin registrar</span>
          </dd>
        </div>

        <div class="kb-campo" *ngIf="comercio.website">
          <dt class="text-sm font-medium text-muted-foreground">Sitio web</dt>
          <dd class="mt-1 truncate text-sm text-foreground">
            <a [href]="comercio.website" target="_blank" rel="noopener noreferrer"
               class="!text-foreground underline decoration-muted-foreground/50 underline-offset-4 hover:decoration-foreground">{{ comercio.website }}</a>
          </dd>
        </div>

      </dl>
      </div>
    </section>

    <!-- ══════════════ DIRECCIÓN DEL PORTAL (URL propia, maqueta aprobada) ═════════════
         La dirección con la que el comercio entra a su portal: acrónimo + dominio. El código del comercio
         no se ve en ella y no cambia nunca. «Cambiar acrónimo» sólo para quien puede editar comercios
         (la consola es de CrediTotal; el núcleo además rechaza a los usuarios de comercio). -->
    <section class="kb-tarjeta" *ngIf="!esProveedor && (comercio.portal?.acronimo || direccionesPortal.length || explicacionDireccion?.estado === 'APAGADA' || (puedeHabilitarDireccion && puede('update')))">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-globe text-muted-foreground" aria-hidden="true"></i>Dirección del portal
          </h2>
          <p class="m-0 text-sm text-muted-foreground">La dirección con la que este comercio entra a su portal.</p>
        </div>
        <span class="kb-badge shrink-0" [ngClass]="chipDireccion.clase">● {{ chipDireccion.texto }}</span>
      </div>
      <div class="kb-tarjeta-cuerpo">
        <p class="text-sm text-muted-foreground" *ngIf="!comercio.portal?.acronimo && !explicacionDireccion && !direccionesPortal.length">Este comercio todavía no tiene dirección propia.</p>
        <ul class="mt-3 divide-y divide-border" *ngIf="direccionesPortal.length">
          <li class="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" *ngFor="let d of direccionesPortal">
            <span class="min-w-0 break-all font-mono">
              <ng-container *ngIf="d.estado === 'disponible'; else sinEnlace"><span class="font-sans text-muted-foreground">Entra hoy por </span><a class="underline underline-offset-4" [href]="d.url" target="_blank" rel="noopener noreferrer">{{ d.url }}</a></ng-container>
              <ng-template #sinEnlace>{{ d.url }}</ng-template>
            </span>
            <span class="kb-badge shrink-0" *ngIf="d.estado === 'pendiente'" [ngClass]="'kb-badge-contorno'">● {{ d.tipo === 'propia' ? 'Dirección propia · en preparación' : 'En preparación' }}</span>
            <span class="kb-badge kb-badge-peligro shrink-0" *ngIf="d.estado === 'con_error'">● {{ d.tipo === 'propia' ? 'Dirección propia · con error' : 'Con error' }}</span>
            <span class="kb-badge kb-badge-exito shrink-0" *ngIf="d.estado === 'disponible'">● Disponible</span>
          </li>
        </ul>
        <dl class="grid gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3" *ngIf="comercio.portal?.acronimo">
          <div class="kb-campo" *ngIf="!direccionesPortal.length">
            <dt class="text-sm font-medium text-muted-foreground">Dirección</dt>
            <dd class="mt-1 break-all text-sm text-foreground">
              <a class="font-mono underline underline-offset-4" [href]="urlPortal" target="_blank" rel="noopener noreferrer">{{ urlPortal }}</a>
            </dd>
          </div>
          <div class="kb-campo">
            <dt class="text-sm font-medium text-muted-foreground">Acrónimo</dt>
            <dd class="mt-1 break-all font-mono text-sm text-foreground">{{ comercio.portal?.acronimo }}</dd>
          </div>
          <div class="kb-campo">
            <dt class="text-sm font-medium text-muted-foreground">Código del comercio</dt>
            <dd class="mt-1 text-sm text-foreground"><span class="font-mono">{{ comercio.code }}</span> <span class="text-muted-foreground">· no cambia nunca</span></dd>
          </div>
          <div class="kb-campo">
            <dt class="text-sm font-medium text-muted-foreground">Dirección anterior</dt>
            <dd class="mt-1 break-all text-sm text-foreground">
              <ng-container *ngIf="anteriorVigente as ant; else sinAnterior">
                <span class="font-mono">{{ hostDe(ant.acronimo) }}</span>
                <span class="text-muted-foreground">· lleva a la nueva hasta el {{ ant.redirige_hasta | date:'dd-MM-yyyy' }}</span>
              </ng-container>
              <ng-template #sinAnterior><span class="text-muted-foreground">—</span></ng-template>
            </dd>
          </div>
        </dl>

        <div class="mt-3 space-y-1 text-sm" *ngIf="explicacionDireccion as ex">
          <p *ngIf="ex.que_pasa"><span class="font-medium text-foreground">Qué pasa:</span> <span [ngClass]="ex.estado === 'ERROR' ? 'kb-texto-peligro' : 'text-muted-foreground'">{{ ex.que_pasa }}</span></p>
          <p *ngIf="ex.que_hacer"><span class="font-medium text-foreground">Qué hacer:</span> <span class="text-muted-foreground">{{ ex.que_hacer }}</span></p>
          <p *ngIf="ex.entra_por"><span class="font-medium text-foreground">Hoy entra por:</span> <span class="font-mono break-all text-muted-foreground">{{ ex.entra_por }}</span></p>
          <p *ngIf="textoRevision as r" class="text-xs text-muted-foreground">{{ r }}</p>
          <p *ngIf="textoActivaDesde as d" class="text-xs text-muted-foreground">{{ d }}</p>
          <details class="text-xs text-muted-foreground" *ngIf="comercio.portal?.error_direccion_google">
            <summary class="cursor-pointer">Detalle técnico</summary>
            <p class="mt-1 break-all font-mono">{{ comercio.portal.error_direccion_google }}</p>
          </details>
        </div>

        <p class="mt-3 text-sm kb-texto-peligro" *ngIf="!explicacionDireccion && comercio.portal?.estado_direccion === 'ERROR' && comercio.portal?.error_direccion">{{ comercio.portal.error_direccion }}</p>

        <p class="mt-3 text-sm text-muted-foreground" *ngIf="ultimaRevision">
          Revisada a las {{ ultimaRevision.hora | date: 'h:mm a' }}: {{ ultimaRevision.texto }}
        </p>
        <div class="mt-4 flex flex-wrap gap-2">
          <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" *ngIf="comercio.portal?.acronimo" [disabled]="probandoDireccion" (click)="probarDireccion()">
            <i class="fas" [ngClass]="probandoDireccion ? 'fa-spinner fa-spin' : 'fa-stethoscope'" aria-hidden="true"></i>Probar dirección
          </button>
          <button type="button" class="kb-btn kb-btn-primary kb-btn-sm" *ngIf="puedeHabilitarDireccion && puede('update')" [disabled]="habilitando" (click)="habilitarDireccion()">
            <i class="fas" [ngClass]="habilitando ? 'fa-spinner fa-spin' : 'fa-globe'" aria-hidden="true"></i>Habilitar dirección propia
          </button>
          <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" *ngIf="comercio.portal?.acronimo && puede('update')" (click)="abrirCambioAcronimo()">Cambiar acrónimo</button>
        </div>

        <div class="mt-4 border-t border-border pt-4" *ngIf="cambioAbierto && comercio.portal?.acronimo">
          <label class="kb-label" for="cdNuevoAcr">Nuevo acrónimo</label>
          <div class="flex items-stretch">
            <input id="cdNuevoAcr" type="text" class="kb-input font-mono" style="max-width:260px;border-top-right-radius:0;border-bottom-right-radius:0"
                   maxlength="63" autocomplete="off" spellcheck="false" [(ngModel)]="nuevoAcronimo" (ngModelChange)="revisarNuevoAcronimo()"
                   [ngClass]="{'kb-input-invalido': estadoNuevo.fase === 'mal'}">
            <span class="flex items-center whitespace-nowrap rounded-r-md border border-l-0 border-border bg-muted px-3 font-mono text-xs text-muted-foreground">.{{ dominio }}</span>
          </div>
          <p class="mt-1.5 text-xs" aria-live="polite"
             [ngClass]="estadoNuevo.fase === 'ok' ? 'kb-texto-exito' : (estadoNuevo.fase === 'mal' ? 'kb-texto-peligro' : 'text-muted-foreground')">
            <ng-container *ngIf="estadoNuevo.fase === 'ok'">✔ Disponible</ng-container>
            <ng-container *ngIf="estadoNuevo.fase === 'mal'">✖ {{ estadoNuevo.mensaje }}</ng-container>
            <ng-container *ngIf="estadoNuevo.fase === 'revisando'">Revisando si está libre…</ng-container>
          </p>
          <div class="kb-aviso kb-aviso-alerta mt-3" role="note">
            <i class="fas fa-exclamation-triangle mt-0.5" aria-hidden="true"></i>
            <div>
              <b>Lo que pasa al cambiarlo:</b> la dirección vieja <span class="font-mono">{{ comercio.portal?.acronimo }}…</span> seguirá llevando a la nueva durante 30 días y después dejará de existir. Las personas del comercio tendrán que volver a iniciar sesión. El código <span class="font-mono">{{ comercio.code }}</span>, las ventas y las liquidaciones no cambian.
            </div>
          </div>
          <div class="mt-3 flex flex-wrap justify-end gap-2">
            <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" (click)="cambioAbierto = false">Cancelar</button>
            <button type="button" class="kb-btn kb-btn-primary kb-btn-sm" [disabled]="estadoNuevo.fase !== 'ok' || cambiandoAcronimo"
                    (click)="cambiarAcronimo()">Cambiar a {{ nuevoAcronimo || '…' }}</button>
          </div>
        </div>
            <div class="kb-modal-fondo" *ngIf="habilitarAbierto">
        <div class="kb-modal">
          <div class="kb-modal-header">
            <div>
              <h3 class="kb-modal-titulo">¿Cuál es el acrónimo de este comercio?</h3>
              <p class="kb-modal-desc">Es lo que va antes del dominio en la dirección de su portal.</p>
            </div>
            <button type="button" class="kb-modal-cerrar" (click)="habilitarAbierto = false"><i class="fas fa-times"></i></button>
          </div>
          <div class="kb-modal-body">
            <label class="kb-label" for="cdHabAcr">Acrónimo</label>
            <div class="flex items-stretch">
              <input id="cdHabAcr" type="text" class="kb-input font-mono" style="max-width:260px;border-top-right-radius:0;border-bottom-right-radius:0"
                     maxlength="63" autocomplete="off" spellcheck="false" [(ngModel)]="nuevoAcronimo" (ngModelChange)="revisarNuevoAcronimo()"
                     [ngClass]="{'kb-input-invalido': estadoNuevo.fase === 'mal'}">
              <span class="flex items-center whitespace-nowrap rounded-r-md border border-l-0 border-border bg-muted px-3 font-mono text-xs text-muted-foreground">.{{ dominio }}</span>
            </div>
            <p class="mt-1.5 text-xs" aria-live="polite"
               [ngClass]="estadoNuevo.fase === 'ok' ? 'kb-texto-exito' : (estadoNuevo.fase === 'mal' ? 'kb-texto-peligro' : 'text-muted-foreground')">
              <ng-container *ngIf="estadoNuevo.fase === 'ok'">✔ Disponible</ng-container>
              <ng-container *ngIf="estadoNuevo.fase === 'mal'">✖ {{ estadoNuevo.mensaje }}</ng-container>
              <ng-container *ngIf="estadoNuevo.fase === 'revisando'">Revisando si está libre…</ng-container>
            </p>
            <p class="mt-2 text-sm kb-texto-peligro" *ngIf="errorHabilitar">{{ errorHabilitar }}</p>
            <div class="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" (click)="habilitarAbierto = false">Cancelar</button>
              <button type="button" class="kb-btn kb-btn-primary kb-btn-sm" [disabled]="estadoNuevo.fase !== 'ok' || habilitando" (click)="confirmarHabilitar()">Habilitar</button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Puesta en marcha del alta: si ya se mandaron los accesos y desde cuándo puede operar. El reenvío se hace en la solicitud. -->
    <section class="kb-tarjeta" *ngIf="puestaAlta as pa">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-rocket text-muted-foreground" aria-hidden="true"></i>Puesta en marcha
          </h2>
          <p class="m-0 text-sm text-muted-foreground">
            Solicitud
            <a class="!text-foreground underline underline-offset-2" *ngIf="pa.solicitud_id" [routerLink]="['/console/altas', pa.solicitud_id]">{{ pa.numero || pa.solicitud_id }}</a>
            <span *ngIf="pa.estado"> · {{ pa.estado }}</span>
          </p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">
        <p class="text-sm font-medium text-foreground" *ngIf="textoOpera(pa) as t">{{ t }}</p>
        <p class="text-sm kb-texto-peligro" *ngIf="!pa.opera_desde && motivoNoOpera(pa) as motivo">Todavía no puede operar: {{ motivo }}</p>
        <p class="text-sm text-muted-foreground" *ngIf="!pa.opera_desde && !motivoNoOpera(pa)">Todavía no puede operar.</p>
        <ul class="mt-3 divide-y divide-border" *ngIf="direccionesPortal.length">
          <li class="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" *ngFor="let d of direccionesPortal">
            <span class="min-w-0 break-all font-mono">
              <ng-container *ngIf="d.estado === 'disponible'; else sinEnlace"><span class="font-sans text-muted-foreground">Entra hoy por </span><a class="underline underline-offset-4" [href]="d.url" target="_blank" rel="noopener noreferrer">{{ d.url }}</a></ng-container>
              <ng-template #sinEnlace>{{ d.url }}</ng-template>
            </span>
            <span class="kb-badge shrink-0" *ngIf="d.estado === 'pendiente'" [ngClass]="'kb-badge-contorno'">● {{ d.tipo === 'propia' ? 'Dirección propia · en preparación' : 'En preparación' }}</span>
            <span class="kb-badge kb-badge-peligro shrink-0" *ngIf="d.estado === 'con_error'">● {{ d.tipo === 'propia' ? 'Dirección propia · con error' : 'Con error' }}</span>
            <span class="kb-badge kb-badge-exito shrink-0" *ngIf="d.estado === 'disponible'">● Disponible</span>
          </li>
        </ul>
        <ul class="mt-3 divide-y divide-border" *ngIf="pa.accesos?.length">
          <li class="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" *ngFor="let a of pa.accesos">
            <span class="min-w-0">
              <span class="font-medium text-foreground">{{ a.nombre }}</span>
              <span class="text-muted-foreground" *ngIf="a.papel"> · {{ a.papel }}</span>
              <span class="block text-xs text-muted-foreground break-all" *ngIf="a.correo">{{ a.correo }}</span>
            </span>
            <span class="text-sm" [ngClass]="a.envio_estado === 'fallo' ? 'kb-texto-peligro' : 'text-muted-foreground'">{{ textoAcceso(a, pa) }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ══════════════ CONDICIÓN VIGENTE ══════════════════════════════════════
         Sin subtítulo: decía «las condiciones se versionan por vigencia: esto
         es la versión que rige hoy», y «vigente» ya lo dice el rótulo. -->
    <!-- Proveedor de servicio (maqueta aprobada): sin tarifario, se le paga a mano. -->
    <section class="kb-tarjeta" *ngIf="esProveedor">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-money-bill-wave text-muted-foreground" aria-hidden="true"></i>Cómo se le paga
          </h2>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo flex flex-col gap-1 text-sm">
        <ng-container *ngIf="!comercio?.terms_vigentes; else tarifarioCargado">
          <p class="m-0"><b>A mano</b>, todavía no tiene tarifario.</p>
          <p class="m-0 text-muted-foreground">Su tarifario se carga en la pestaña Condiciones; queda guardado pero no se usa para liquidar.</p>
        </ng-container>
        <ng-template #tarifarioCargado>
          <p class="m-0"><b>A mano.</b> Tarifario cargado, todavía no se usa.</p>
          <p class="m-0 text-muted-foreground">La liquidación automática no toma a los proveedores de servicio.</p>
        </ng-template>
      </div>
    </section>

    <section class="kb-tarjeta" *ngIf="!esProveedor">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-file-signature text-muted-foreground" aria-hidden="true"></i>Condición vigente
          </h2>
          <p class="m-0 text-sm text-muted-foreground">La que rige hoy; las anteriores, en el historial.</p>
        </div>
        <!-- En la cabecera y no en el cuerpo: es la acción de esta tarjeta, y
             `kb-card-header` ya reparte el título a la izquierda y esto a la
             derecha. Metido entre los datos, competía con ellos. -->
        <a class="kb-tarjeta-accion kb-btn kb-btn-outline kb-btn-sm shrink-0"
           [routerLink]="['/console/merchants', codigo, 'condiciones']">Ver el historial</a>
      </div>
      <div class="kb-tarjeta-cuerpo">

      <dl class="grid gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Comisión</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <!-- La cifra del acuerdo, grande como el «Sin límite» del Gateway. -->
          <span class="text-2xl font-semibold tabular-nums" *ngIf="!sinComision">{{ comision() }}</span>
          <span class="kb-dato-nota mt-0.5 block tabular-nums" *ngIf="!sinComision && otrosPlazos()">a {{ plazoCobro() }} · {{ otrosPlazos() }}</span>
          <span class="text-foreground" *ngIf="sinComision && rigeDesde">Aprobada, rige desde el {{ rigeDesde }}</span>
          <span class="kb-dato-falta" *ngIf="sinComision && !rigeDesde">
            <i class="fas fa-exclamation-circle mr-1.5" aria-hidden="true"></i>Sin definir
          </span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Base de cálculo</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span  *ngIf="!sinComision">{{ baseComision() }}</span>
          <span class="text-muted-foreground" *ngIf="sinComision">—</span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Plazo de cobro</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span>{{ plazoCobro() }}</span>
          <span class="kb-dato-nota mt-0.5 block" *ngIf="!sinComision">es cuándo cobra el comercio</span>
          </dd>
        </div>

        <!-- 1.6b (08-oct): el modelo de negocio decide cómo se liquida (MOD-01). El modelo de riesgo
             es «Sin recurso» para todos: queda como nota. -->
        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Esquema de liquidación</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span *ngIf="modelo()">{{ modelo() }}</span>
          <span class="kb-dato-falta" *ngIf="!modelo() && comercio?.terms_vigentes">
            <i class="fas fa-exclamation-circle mr-1.5" aria-hidden="true"></i>Sin definir: sin modelo no se liquida
          </span>
          <span class="text-muted-foreground" *ngIf="!comercio?.terms_vigentes">—</span>
          <span class="kb-dato-nota mt-0.5 block" *ngIf="riesgo() !== '—'">riesgo: {{ riesgo() | lowercase }}</span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Día no hábil</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span>{{ diaHabil() }}</span>
          <span class="kb-dato-nota mt-0.5 block" *ngIf="comercio?.terms_vigentes">si el pago cae en fin de semana o feriado</span>
          </dd>
        </div>

        <!-- Sólo en cero, donde avisa; con tiendas, el número ya está en la pestaña. -->
        <div class="kb-campo" *ngIf="!tiendas.length">
          <dt class="text-sm font-medium text-muted-foreground">Tiendas asignadas</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span class="tabular-nums">{{ tiendas.length }}</span>
          <span class="kb-dato-nota mt-0.5 block" *ngIf="!tiendas.length">sin tiendas no puede vender</span>
          </dd>
        </div>

        <div class="kb-campo">
          <dt class="text-sm font-medium text-muted-foreground">Vigente desde</dt>
          <dd class="mt-1 break-words text-sm text-foreground">
          <span class="tabular-nums" *ngIf="vigenteDesde">
            {{ vigenteDesde | date: 'dd/MM/yyyy':'UTC' }}
          </span>
          <span class="kb-dato-nota mt-0.5 block" *ngIf="nombreVersion">{{ nombreVersion }}</span>
          <span class="text-muted-foreground" *ngIf="!vigenteDesde">—</span>
          </dd>
        </div>
      </dl>
      </div>
    </section>

    <!-- ══════════════ DOCUMENTOS ═════════════════════════════════════════════
         2026-09-24: cada tipo en su renglón con su estado (antes, dos chips dentro de
         Identificación y no se veía cuál faltaba). -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-paperclip text-muted-foreground" aria-hidden="true"></i>Documentos
          </h2>
          <p class="m-0 text-sm text-muted-foreground">Ninguno frena el envío de la ficha; quien revisa los pide si hacen falta.</p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">
        <ul class="m-0 list-none divide-y divide-border p-0">
          <li class="!my-0 flex min-h-[44px] flex-wrap items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
              *ngFor="let d of DOCUMENTOS">
            <span class="text-sm font-medium text-foreground">{{ d.label }}</span>
            <span class="flex flex-wrap items-center gap-2">
              <a *ngFor="let f of documentosDe(d.kind)" [href]="urlArchivo(f.url)" target="_blank" rel="noopener"
                 class="kb-btn kb-btn-ghost kb-btn-sm h-7" [title]="f.name">
                <i class="fas fa-eye !text-foreground" aria-hidden="true"></i>Ver
              </a>
              <span class="kb-badge kb-badge-exito" *ngIf="documentosDe(d.kind).length">
                <i class="fas fa-check-circle" aria-hidden="true"></i>Cargado</span>
              <span class="kb-badge kb-badge-alerta" *ngIf="!documentosDe(d.kind).length && d.obligatorio">
                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>Falta</span>
              <span class="text-sm text-muted-foreground" *ngIf="!documentosDe(d.kind).length && !d.obligatorio">Sin cargar</span>
            </span>
          </li>
          <li class="!my-0 flex min-h-[44px] flex-wrap items-center justify-between gap-3 py-2 last:pb-0"
              *ngFor="let f of otrosDocumentos">
            <span class="text-sm font-medium text-foreground">Otro documento</span>
            <a [href]="urlArchivo(f.url)" target="_blank" rel="noopener" class="kb-btn kb-btn-ghost kb-btn-sm h-7" [title]="f.name">
              <i class="fas fa-eye !text-foreground" aria-hidden="true"></i>Ver</a>
          </li>
        </ul>
      </div>
    </section>

    <!-- ══════════════ EJECUTIVO DE CREDITOTAL (EJE-01; Mario, 14-oct) ═════════════ -->
    <section class="kb-tarjeta self-start" *ngIf="!esProveedor">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-headset text-muted-foreground" aria-hidden="true"></i>Ejecutivo de CrediTotal
          </h2>
          <p class="m-0 text-sm text-muted-foreground">Lo que el comercio ve en su portal cuando un aviso le dice «coméntalo a tu ejecutivo». Sin ejecutivo, ve el contacto general de atención a comercios.</p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo flex flex-col gap-3 text-sm">
        <p class="m-0" *ngIf="ejecutivo?.ejecutivo"><b>{{ ejecutivo.ejecutivo.nombre }}</b>
          <span class="text-muted-foreground" *ngIf="ejecutivo.contacto"> · {{ mediosEjecutivo() }}</span></p>
        <p class="m-0 text-muted-foreground" *ngIf="!ejecutivo?.ejecutivo">
          Sin ejecutivo asignado<ng-container *ngIf="ejecutivo?.contacto">: el comercio ve «{{ ejecutivo.contacto.nombre }}»</ng-container><ng-container *ngIf="!ejecutivo?.contacto"> y tampoco hay contacto general (Parámetros › Atención a comercios)</ng-container>.</p>
        <div class="flex flex-wrap items-end gap-2" *ngIf="puede('update')">
          <label class="kb-label">Correo o código del usuario
            <input type="text" class="kb-input !w-[260px] max-w-full" [(ngModel)]="usuarioEjecutivo" name="usuarioEjecutivo" placeholder="ana@creditotal.online" autocomplete="off"></label>
          <button type="button" class="kb-btn kb-btn-sm !w-auto !bg-foreground !text-background" [disabled]="!usuarioEjecutivo.trim() || guardandoEjecutivo" (click)="asignarEjecutivo(usuarioEjecutivo)">Asignar</button>
          <button type="button" class="kb-btn kb-btn-outline kb-btn-sm !w-auto" *ngIf="ejecutivo?.ejecutivo" [disabled]="guardandoEjecutivo" (click)="asignarEjecutivo(null)">Quitar</button>
        </div>
        <p class="kb-aviso kb-aviso-alerta m-0" *ngIf="errorEjecutivo" role="alert">{{ errorEjecutivo }}</p>
      </div>
    </section>

    <!-- ══════════════ CÓDIGO DEL GERENTE EN LA CAJA (2026-10-08) ═════════════ -->
    <section class="kb-tarjeta" *ngIf="codigoGerente && !esProveedor">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-key text-muted-foreground" aria-hidden="true"></i>Código del gerente en la caja
          </h2>
          <p class="m-0 text-sm text-muted-foreground">Cuándo el cajero necesita el código que da el gerente. El comercio también lo cambia desde su portal.</p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">
        <ul class="m-0 list-none divide-y divide-border p-0">
          <li class="!my-0 flex min-h-[52px] items-center justify-between gap-3 py-2"
              *ngFor="let f of [{ clave: 'turno', titulo: 'Al abrir el turno' }, { clave: 'anular', titulo: 'Al anular una venta' }]">
            <span>
              <span class="block text-sm font-medium text-foreground">{{ f.titulo }}</span>
              <span class="block text-xs text-muted-foreground">{{ $any(codigoGerente)[f.clave] ? 'Lo pide' : 'No lo pide' }}</span>
            </span>
            <button type="button" role="switch" [attr.aria-checked]="$any(codigoGerente)[f.clave]" [attr.aria-label]="f.titulo"
                    [disabled]="guardandoCodigo" (click)="cambiarCodigoGerente($any(f.clave))"
                    class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
                    [ngClass]="$any(codigoGerente)[f.clave] ? 'bg-primary' : 'bg-input'">
              <span class="inline-block h-5 w-5 rounded-full bg-background shadow transition-transform"
                    [ngClass]="$any(codigoGerente)[f.clave] ? 'translate-x-[22px]' : 'translate-x-[2px]'"></span>
            </button>
          </li>
        </ul>
      </div>
    </section>

    <!-- ══════════════ RASTRO DE APROBACIÓN ═══════════════════════════════════
         Es lo que se pide el día que algo sale mal.

         🔴 SIN EL NOMBRE DE QUIÉN LO HIZO, a propósito: `profile_history[].by`
         llega como identificador sin poblar (el detalle sólo popula
         `organization`, ver `controllers/merchant.js:57`), así que decir «la
         aprobó el gerente» era nombrar un cargo sin saber quién fue. Antes que
         un actor inventado, la fecha sola. -->
    <section class="kb-tarjeta" *ngIf="historial.length">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-clipboard-check text-muted-foreground" aria-hidden="true"></i>Rastro de aprobación
          </h2>
          <p class="m-0 text-sm text-muted-foreground">Cuándo se envió y cuándo se aprobó la ficha.</p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">

      <dl class="grid gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <ng-container *ngFor="let h of historial">
          <div class="kb-campo">
            <dt class="text-sm font-medium text-muted-foreground">{{ ACCIONES[h.action] || 'Movimiento' }}</dt>
            <dd class="mt-1 break-words text-sm text-foreground">
            <span class="tabular-nums">{{ h.at | date: 'dd/MM/yyyy HH:mm' }}</span>
            <span class="kb-dato-nota mt-0.5 block" *ngIf="h.notes">{{ h.notes }}</span>
            </dd>
          </div>
        </ng-container>
      </dl>
      </div>
    </section>

    </div><!-- /la grilla del detalle -->

  </ng-container>
</div>
